import React, { useState } from 'react';
import { resetPassword } from '../../agents/authAgent';
import './AuthPage.css';

/**
 * ResetPasswordPage: Enter new password using the token from the email link.
 * Props:
 *   token        - reset token (from URL query param)
 *   onSuccess()  - called after a successful reset (navigate to login)
 */
const ResetPasswordPage = ({ token, onSuccess }) => {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      await resetPassword(token, password);
      setDone(true);
    } catch (err) {
      setError(err.message || 'This reset link is invalid or has expired.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <h1 className="auth-title">Reset password</h1>
        <p className="auth-subtitle">Choose a new password for your account.</p>

        {error && (
          <div className="auth-error" role="alert">
            {error}
          </div>
        )}

        {done ? (
          <>
            <div className="auth-success" role="status">
              Your password has been reset successfully.
            </div>
            <p className="auth-switch" style={{ marginTop: '16px' }}>
              <button
                type="button"
                className="auth-link-btn"
                onClick={onSuccess}
              >
                Sign in with your new password →
              </button>
            </p>
          </>
        ) : (
          <form className="auth-form" onSubmit={handleSubmit} noValidate>
            <div className="auth-field">
              <label htmlFor="reset-password" className="auth-label">New password</label>
              <div className="auth-input-wrap">
                <input
                  id="reset-password"
                  type={showPassword ? 'text' : 'password'}
                  className="auth-input auth-input--with-toggle"
                  placeholder="At least 8 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                  disabled={submitting}
                />
                <button
                  type="button"
                  className="auth-password-toggle"
                  onClick={() => setShowPassword((s) => !s)}
                  disabled={submitting}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            <div className="auth-field">
              <label htmlFor="reset-confirm" className="auth-label">Confirm password</label>
              <div className="auth-input-wrap">
                <input
                  id="reset-confirm"
                  type={showPassword ? 'text' : 'password'}
                  className="auth-input auth-input--with-toggle"
                  placeholder="Repeat your new password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  autoComplete="new-password"
                  required
                  disabled={submitting}
                />
                <button
                  type="button"
                  className="auth-password-toggle"
                  onClick={() => setShowPassword((s) => !s)}
                  disabled={submitting}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="auth-btn-primary"
              disabled={submitting}
            >
              {submitting ? 'Saving…' : 'Set new password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;
